import { mergeAttributes, Node } from "@tiptap/core"
import { NodeSelection, TextSelection } from "@tiptap/pm/state"

import { enterMovesToNextLine } from "./line-navigation"

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		frame: {
			insertFrame: () => ReturnType
		}
	}
}

// Above Tiptap's own figure and paragraph rules.
const PARSE_PRIORITY = 1000

/** Text before the frame, outside its box: Mintlify's `hint`. */
export const FrameHintExtension = Node.create({
	name: "frameHint",
	content: "inline*",
	marks: "_",
	defining: true,

	parseHTML() {
		return [{ tag: "p[data-frame-hint]", priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"p",
			mergeAttributes(HTMLAttributes, {
				"data-frame-hint": "",
				class: "pl-frame-hint",
			}),
			0,
		]
	},
})

export const FrameMediaExtension = Node.create({
	name: "frameMedia",
	content: "block+",
	defining: true,

	parseHTML() {
		return [{ tag: "div[data-frame-media]", priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-frame-media": "",
				class: "pl-frame-media",
			}),
			0,
		]
	},
})

/** Mintlify's `caption`, which takes links and bold text. */
export const FrameCaptionExtension = Node.create({
	name: "frameCaption",
	content: "inline*",
	marks: "_",
	defining: true,

	parseHTML() {
		return [{ tag: "figcaption[data-frame-caption]", priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"figcaption",
			mergeAttributes(HTMLAttributes, {
				"data-frame-caption": "",
				class: "pl-frame-caption",
			}),
			0,
		]
	},
})

export const FrameFigureExtension = Node.create({
	name: "frameFigure",
	content: "frameMedia frameCaption?",
	defining: true,

	parseHTML() {
		return [
			{ tag: 'figure[data-type="frame-figure"]', priority: PARSE_PRIORITY },
		]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"figure",
			mergeAttributes(HTMLAttributes, {
				"data-type": "frame-figure",
				class: "pl-frame-figure",
			}),
			0,
		]
	},
})

export const FrameExtension = Node.create({
	name: "frame",
	// Its keys run before the core Enter chain, which tries to split the line.
	priority: 200,
	group: "block",
	content: "frameHint? frameFigure",
	defining: true,
	isolating: true,

	parseHTML() {
		return [{ tag: 'div[data-type="frame"]', priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "frame",
				class: "pl-frame",
			}),
			0,
		]
	},

	addKeyboardShortcuts() {
		return {
			Enter: () => enterMovesToNextLine(this.editor, ["frameHint"]),
		}
	},

	addCommands() {
		return {
			// A selected image or video goes into the frame; otherwise it starts empty.
			insertFrame:
				() =>
				({ state, tr, dispatch }) => {
					const { frame, frameFigure, frameMedia, frameCaption, paragraph } =
						state.schema.nodes
					if (
						!frame ||
						!frameFigure ||
						!frameMedia ||
						!frameCaption ||
						!paragraph
					) {
						return false
					}
					const { selection } = state
					const wrapped =
						selection instanceof NodeSelection &&
						["image", "video"].includes(selection.node.type.name)
							? selection.node
							: null
					const node = frame.create(null, [
						frameFigure.create(null, [
							frameMedia.create(null, wrapped ?? paragraph.create()),
							frameCaption.create(),
						]),
					])
					if (!dispatch) return true
					const { $from } = selection
					const inBlock = $from.depth > 0
					const after = inBlock ? $from.after() : selection.to
					const emptyLine =
						!wrapped &&
						inBlock &&
						$from.parent.isTextblock &&
						$from.parent.content.size === 0
					const from = wrapped
						? selection.from
						: emptyLine
							? $from.before()
							: after
					const to = wrapped ? selection.to : after
					tr.replaceWith(from, to, node)
					const start = from
					// Frame, figure and media each open one position before the media's content.
					const inner = start + 3
					const mediaSize = node.firstChild?.firstChild?.nodeSize ?? 0
					// A wrapped image leaves the caption to write; an empty frame, the media.
					const caret = wrapped ? inner + mediaSize : inner + 1
					tr.setSelection(TextSelection.near(tr.doc.resolve(caret)))
					tr.scrollIntoView()
					return true
				},
		}
	},
})

export const FrameExtensions = [
	FrameExtension,
	FrameHintExtension,
	FrameFigureExtension,
	FrameMediaExtension,
	FrameCaptionExtension,
]
